class MemoryGame extends HTMLElement {
  constructor() {
    super()

    // --- Game State Variables ---
    this.flippedCards = []
    this.matchedPairs = 0
    this.isBoardLocked = false
  }

  connectedCallback() {
    const dataScript = this.querySelector('script[type="application/json"][data-role="game-data"]')
    if (dataScript) {
      try {
        this.cardImageSources = JSON.parse(dataScript.textContent)
        this.totalPairs = this.cardImageSources.length
        this.initializeGame()
      } catch (e) {
        console.error('Invalid game data', e)
      }
    }
  }

  initializeGame() {
    // Get references to the elements we just created in render()
    this.gameBoard = this.querySelector('[data-role="board"]')
    const resetButton = this.querySelector('[data-role="reset-btn"]')

    // Clear any existing content and reset state
    this.gameBoard.innerHTML = ''
    this.flippedCards = []
    this.matchedPairs = 0
    this.isBoardLocked = false

    // Create and shuffle the deck
    const cardData = [...this.cardImageSources, ...this.cardImageSources].map((url) => ({
      imageUrl: url,
      pairId: url
    }))
    this.shuffle(cardData)

    // Create card elements and add them to the board
    cardData.forEach((data) => {
      const card = this.createCardElement(data)
      this.gameBoard.appendChild(card)
    })

    // Add event listener for the reset button
    resetButton?.addEventListener('click', () => this.initializeGame())
  }

  createCardElement(data) {
    const card = document.createElement('div')
    card.className = 'bbs-sg-mg__card'
    card.dataset.pairId = data.pairId

    card.innerHTML = `
      <div class="bbs-sg-mg__card-face bbs-sg-mg__card-face--front">
        <img src="${data.imageUrl}" alt="Card Face" class="bbs-sg-mg__card-image">
      </div>
      <div class="bbs-sg-mg__card-face bbs-sg-mg__card-face--back">&nbsp;</div>
    `

    card.addEventListener('click', () => this.handleCardClick(card))
    return card
  }

  handleCardClick(card) {
    if (this.isBoardLocked || card.classList.contains('is-flipped') || card.classList.contains('is-matched')) {
      return
    }

    this.flip(card)
    this.flippedCards.push(card)

    if (this.flippedCards.length === 2) {
      this.checkForMatch()
    }
  }

  checkForMatch() {
    this.isBoardLocked = true
    const [card1, card2] = this.flippedCards

    if (card1.dataset.pairId === card2.dataset.pairId) {
      setTimeout(() => {
        this.match(card1)
        this.match(card2)
        this.matchedPairs++
        this.resetTurn()
        if (this.matchedPairs === this.totalPairs) {
          // Dispatch the same event as the crossword game
          this.dispatchEvent(new CustomEvent('summerGamesWon', { bubbles: true, composed: true }))
        }
      }, 600)
    } else {
      setTimeout(() => {
        this.unflip(card1)
        this.unflip(card2)
        this.resetTurn()
      }, 1200)
    }
  }

  resetTurn() {
    this.flippedCards = []
    this.isBoardLocked = false
  }

  // --- Card action methods ---
  flip(card) {
    card.classList.add('is-flipped')
  }
  unflip(card) {
    card.classList.add('is-unflipping')
    // Remove is-flipped immediately but let the animation play
    card.classList.remove('is-flipped')
    // Clean up the unflipping class after animation completes
    setTimeout(() => {
      card.classList.remove('is-unflipping')
    }, 600)
  }
  match(card) {
    card.classList.add('is-matched')
  }

  /**
   * Shuffles an array in place using the Fisher-Yates algorithm.
   * @param {Array} array The array to shuffle.
   */
  shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[array[i], array[j]] = [array[j], array[i]]
    }
  }
}

// Define the custom element so the browser recognizes <memory-game>
customElements.define('memory-game', MemoryGame)